import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout } from "./authSlice";
import { clearLicenseExpired, setLicenseExpired } from "./licenseSlice";

const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://devapi.sordiz.com";
const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const getRequestUrl = (args) =>
  typeof args === "string" ? args : args?.url || "";

const isAdminRequest = (args) => getRequestUrl(args).startsWith("/admin/");

const isAuthRequest = (args) => getRequestUrl(args).includes("/admin/auth/");

const getResponseCode = (result) => {
  const data = result?.error?.data || {};
  const code = data.code || data.errorCode || data.title;
  return typeof code === "string" ? code.trim().toUpperCase() : "";
};

const isProblemDetails = (data) => {
  if (!data || typeof data !== "object") return false;
  const hasTitle = typeof data.title === "string" && data.title.trim();
  const hasDetail = typeof data.detail === "string";
  const hasStatus =
    typeof data.status === "number" ||
    (typeof data.status === "string" && data.status.trim());
  return Boolean(hasTitle && hasDetail && hasStatus);
};

const getBusinessIdFromRequest = (args) => {
  const requestUrl = getRequestUrl(args);
  const match = requestUrl.match(/\/admin\/platform\/businesses\/([^/]+)/i);
  return match?.[1] || null;
};

const baseQueryWithUnauthorizedHandler = async (
  args,
  baseQueryApi,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, baseQueryApi, extraOptions);

  if (!result?.error && isProblemDetails(result?.data)) {
    const statusValue =
      typeof result.data.status === "number"
        ? result.data.status
        : Number(result.data.status);
    result = {
      error: {
        status: Number.isNaN(statusValue) ? 200 : statusValue,
        data: result.data,
      },
    };
  }

  const status = result?.error?.status;
  const originalStatus = result?.error?.originalStatus;
  const isUnauthorized = status === 401 || originalStatus === 401;
  const responseCode = getResponseCode(result);
  const isLicenseExpired = responseCode === "LICENSE_EXPIRED";
  const isBusinessContextMissing = responseCode === "BUSINESS_ID_REQUIRED";

  if ((isUnauthorized || isBusinessContextMissing) && !isAuthRequest(args)) {
    const authState = baseQueryApi.getState()?.auth;
    if (authState?.token || authState?.user) {
      baseQueryApi.dispatch(logout());
      baseQueryApi.dispatch(clearLicenseExpired());
    }
  }

  if (isLicenseExpired) {
    baseQueryApi.dispatch(
      setLicenseExpired({
        code: responseCode,
        message:
          result?.error?.data?.detail ||
          "License expired. Please renew to continue.",
        businessId: getBusinessIdFromRequest(args),
      }),
    );
  } else if (!result?.error && isAdminRequest(args) && !isAuthRequest(args)) {
    baseQueryApi.dispatch(clearLicenseExpired());
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithUnauthorizedHandler,
  tagTypes: [
    "Business",
    "BusinessGeneralSettings",
    "Store",
    "License",
    "Category",
    "Item",
    "ModifierGroup",
    "Bundle",
    "Promotion",
    "UpsellCampaign",
    "Tag",
    "Allergen",
    "ProteinType",
    "User",
    "Role",
    "Permission",
    "UserRole",
    "RolePermission",
    "Menu",
    "OrderType",
    "Branding",
    "Floor",
    "Section",
    "Table",
    "QRConfig",
    "Reporting",
    "DayOverride",
    "AvailabilityWindow",
    "Slot",
    "SlotDay",
    "ItemSlot",
    "Reservation",
  ],
  endpoints: (builder) => ({
    // Auth
    loginSuperAdmin: builder.mutation({
      query: (payload) => ({
        url: "/admin/auth/super-admin/login",
        method: "POST",
        body: payload,
      }),
    }),
    loginBusinessAdmin: builder.mutation({
      query: (payload) => ({
        url: "/admin/auth/business-admin/login",
        method: "POST",
        body: payload,
      }),
    }),

    // Businesses
    getBusinesses: builder.query({
      query: () => "/admin/platform/businesses",
      providesTags: ["Business"],
    }),
    getBusiness: builder.query({
      query: (businessId) => `/admin/platform/businesses/${businessId}`,
      providesTags: ["Business"],
    }),
    createBusiness: builder.mutation({
      query: (payload) => ({
        url: "/admin/platform/businesses",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Business"],
    }),
    updateBusiness: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Business"],
    }),
    getBusinessStores: builder.query({
      query: (businessId) =>
        `/admin/platform/businesses/${businessId}/stores`,
      providesTags: ["Store"],
    }),
    getBusinessStore: builder.query({
      query: ({ businessId, storeId }) =>
        `/admin/platform/businesses/${businessId}/stores/${storeId}`,
      providesTags: ["Store"],
    }),
    createBusinessStore: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/stores`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Store", "Business"],
    }),
    updateBusinessStore: builder.mutation({
      query: ({ businessId, storeId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/stores/${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Store", "Business"],
    }),
    updateBusinessStoreStatus: builder.mutation({
      query: ({ businessId, storeId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/stores/${storeId}/status`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Store", "Business"],
    }),
    getStoreFeatures: builder.query({
      query: (storeId) => `/admin/stores/${storeId}/features`,
      providesTags: ["Store"],
    }),
    updateStoreFeatures: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/stores/${storeId}/features`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Store"],
    }),
    uploadBusinessLogo: builder.mutation({
      query: ({ businessId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/admin/platform/businesses/${businessId}/logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Business"],
    }),
    deleteBusinessLogo: builder.mutation({
      query: (businessId) => ({
        url: `/admin/platform/businesses/${businessId}/logo`,
        method: "DELETE",
      }),
      invalidatesTags: ["Business"],
    }),

    // Business Features
    getBusinessFeatures: builder.query({
      query: (businessId) =>
        `/admin/platform/businesses/${businessId}/features`,
      providesTags: ["Business"],
    }),
    updateBusinessFeatures: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/features`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Business"],
    }),

    // Business General Settings
    getBusinessGeneralSettings: builder.query({
      query: () => "/admin/business/general-settings",
      providesTags: ["BusinessGeneralSettings"],
    }),
    updateBusinessGeneralSettings: builder.mutation({
      query: (payload) => ({
        url: "/admin/business/general-settings",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["BusinessGeneralSettings"],
    }),
    getBusinessTipPresets: builder.query({
      query: () => "/admin/business/general-settings/tip-presets",
      providesTags: ["BusinessGeneralSettings"],
    }),
    updateBusinessTipPresets: builder.mutation({
      query: (payload) => ({
        url: "/admin/business/general-settings/tip-presets",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["BusinessGeneralSettings"],
    }),

    // Payment Gateways
    getPaymentGateways: builder.query({
      query: (businessId) =>
        `/admin/platform/businesses/${businessId}/payment-gateways`,
      providesTags: ["Business"],
    }),
    updateStripeGateway: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/payment-gateways/stripe`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Business"],
    }),

    // Licenses
    getLicenses: builder.query({
      query: (businessId) =>
        `/admin/platform/businesses/${businessId}/licenses`,
      providesTags: ["License"],
    }),
    allocateLicense: builder.mutation({
      query: ({ businessId, payload }) => ({
        url: `/admin/platform/businesses/${businessId}/licenses`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["License"],
    }),

    // Categories
    getCategories: builder.query({
      query: (storeId) => `/admin/catalog/categories?storeId=${storeId}`,
      providesTags: ["Category"],
    }),
    createCategory: builder.mutation({
      query: ({ storeId, payload, file }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        if (file) {
          formData.append("file", file);
        }
        return {
          url: `/admin/catalog/categories?storeId=${storeId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    updateCategory: builder.mutation({
      query: ({ storeId, categoryId, payload, file, removeImage }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        if (file) {
          formData.append("file", file);
        }
        if (removeImage) {
          formData.append("removeImage", "true");
        }
        return {
          url: `/admin/catalog/categories/${categoryId}?storeId=${storeId}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    deleteCategory: builder.mutation({
      query: ({ storeId, categoryId }) => ({
        url: `/admin/catalog/categories/${categoryId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),

    // Items
    getItems: builder.query({
      query: (storeId) => `/admin/catalog/items?storeId=${storeId}`,
      providesTags: ["Item"],
    }),
    createItem: builder.mutation({
      query: ({ storeId, payload, file }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        if (file) {
          formData.append("file", file);
        }
        return {
          url: `/admin/catalog/items?storeId=${storeId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Item"],
    }),
    updateItem: builder.mutation({
      query: ({ storeId, itemId, payload, file, removeImage }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        if (file) {
          formData.append("file", file);
        }
        if (removeImage) {
          formData.append("removeImage", "true");
        }
        return {
          url: `/admin/catalog/items/${itemId}?storeId=${storeId}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Item"],
    }),
    deleteItem: builder.mutation({
      query: ({ storeId, itemId }) => ({
        url: `/admin/catalog/items/${itemId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Item"],
    }),

    // Modifier Groups
    getModifierGroups: builder.query({
      query: (storeId) => `/admin/catalog/modifier-groups?storeId=${storeId}`,
      providesTags: ["ModifierGroup"],
    }),
    createModifierGroup: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/modifier-groups?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ModifierGroup"],
    }),
    updateModifierGroup: builder.mutation({
      query: ({ storeId, groupId, payload }) => ({
        url: `/admin/catalog/modifier-groups/${groupId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ModifierGroup"],
    }),
    deleteModifierGroup: builder.mutation({
      query: ({ storeId, groupId }) => ({
        url: `/admin/catalog/modifier-groups/${groupId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ModifierGroup"],
    }),

    // Modifier Options
    getModifierOptions: builder.query({
      query: ({ storeId, groupId }) =>
        `/admin/catalog/modifier-groups/${groupId}/options?storeId=${storeId}`,
      providesTags: ["ModifierGroup"],
    }),
    createModifierOption: builder.mutation({
      query: ({ storeId, groupId, payload }) => ({
        url: `/admin/catalog/modifier-groups/${groupId}/options?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ModifierGroup"],
    }),
    updateModifierOption: builder.mutation({
      query: ({ storeId, groupId, optionId, payload }) => ({
        url: `/admin/catalog/modifier-groups/${groupId}/options/${optionId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ModifierGroup"],
    }),
    deleteModifierOption: builder.mutation({
      query: ({ storeId, groupId, optionId }) => ({
        url: `/admin/catalog/modifier-groups/${groupId}/options/${optionId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ModifierGroup"],
    }),

    // Attach Modifier Groups to Items
    updateItemModifierGroups: builder.mutation({
      query: ({ storeId, itemId, payload }) => ({
        url: `/admin/catalog/items/${itemId}/modifier-groups?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Item", "ModifierGroup"],
    }),

    // Bundles
    getBundles: builder.query({
      query: (storeId) => `/admin/catalog/bundles?storeId=${storeId}`,
      providesTags: ["Bundle"],
    }),
    getBundle: builder.query({
      query: ({ storeId, bundleId }) =>
        `/admin/catalog/bundles/${bundleId}?storeId=${storeId}`,
      providesTags: ["Bundle"],
    }),
    getBundleTypes: builder.query({
      query: () => "/admin/catalog/bundles/types",
      providesTags: ["Bundle"],
    }),
    createBundle: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/bundles?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    updateBundle: builder.mutation({
      query: ({ storeId, bundleId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    deleteBundle: builder.mutation({
      query: ({ storeId, bundleId }) => ({
        url: `/admin/catalog/bundles/${bundleId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bundle"],
    }),

    // Bundle Variants
    createBundleVariant: builder.mutation({
      query: ({ storeId, bundleId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    updateBundleVariant: builder.mutation({
      query: ({ storeId, bundleId, variantId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    deleteBundleVariant: builder.mutation({
      query: ({ storeId, bundleId, variantId }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bundle"],
    }),

    // Bundle Components
    createBundleComponent: builder.mutation({
      query: ({ storeId, bundleId, variantId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    updateBundleComponent: builder.mutation({
      query: ({ storeId, bundleId, variantId, componentId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components/${componentId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    deleteBundleComponent: builder.mutation({
      query: ({ storeId, bundleId, variantId, componentId }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components/${componentId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bundle"],
    }),

    // Bundle Component Items
    createBundleComponentItem: builder.mutation({
      query: ({ storeId, bundleId, variantId, componentId, payload }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components/${componentId}/items?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    updateBundleComponentItem: builder.mutation({
      query: ({
        storeId,
        bundleId,
        variantId,
        componentId,
        componentItemId,
        payload,
      }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components/${componentId}/items/${componentItemId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Bundle"],
    }),
    deleteBundleComponentItem: builder.mutation({
      query: ({
        storeId,
        bundleId,
        variantId,
        componentId,
        componentItemId,
      }) => ({
        url: `/admin/catalog/bundles/${bundleId}/variants/${variantId}/components/${componentId}/items/${componentItemId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bundle"],
    }),

    // Category Bundles
    createCategoryBundle: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/category-bundles?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Bundle", "Category"],
    }),
    updateCategoryBundle: builder.mutation({
      query: ({ storeId, categoryBundleId, payload }) => ({
        url: `/admin/catalog/category-bundles/${categoryBundleId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Bundle", "Category"],
    }),
    deleteCategoryBundle: builder.mutation({
      query: ({ storeId, categoryBundleId }) => ({
        url: `/admin/catalog/category-bundles/${categoryBundleId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Bundle", "Category"],
    }),

    // Promotions
    getPromotions: builder.query({
      query: (storeId) => `/admin/catalog/promotions?storeId=${storeId}`,
      providesTags: ["Promotion"],
    }),
    getPromotion: builder.query({
      query: ({ storeId, promotionId }) =>
        `/admin/catalog/promotions/${promotionId}?storeId=${storeId}`,
      providesTags: ["Promotion"],
    }),
    createPromotion: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/promotions?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    updatePromotion: builder.mutation({
      query: ({ storeId, promotionId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    deletePromotion: builder.mutation({
      query: ({ storeId, promotionId }) => ({
        url: `/admin/catalog/promotions/${promotionId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Promotion"],
    }),

    // Promotion Conditions
    createPromotionCondition: builder.mutation({
      query: ({ storeId, promotionId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/conditions?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    updatePromotionCondition: builder.mutation({
      query: ({ storeId, promotionId, conditionId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/conditions/${conditionId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    deletePromotionCondition: builder.mutation({
      query: ({ storeId, promotionId, conditionId }) => ({
        url: `/admin/catalog/promotions/${promotionId}/conditions/${conditionId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Promotion"],
    }),

    // Promotion Effects
    createPromotionEffect: builder.mutation({
      query: ({ storeId, promotionId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/effects?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    updatePromotionEffect: builder.mutation({
      query: ({ storeId, promotionId, effectId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/effects/${effectId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    deletePromotionEffect: builder.mutation({
      query: ({ storeId, promotionId, effectId }) => ({
        url: `/admin/catalog/promotions/${promotionId}/effects/${effectId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Promotion"],
    }),

    // Promotion Schedules
    createPromotionSchedule: builder.mutation({
      query: ({ storeId, promotionId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/schedules?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    updatePromotionSchedule: builder.mutation({
      query: ({ storeId, promotionId, scheduleId, payload }) => ({
        url: `/admin/catalog/promotions/${promotionId}/schedules/${scheduleId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Promotion"],
    }),
    deletePromotionSchedule: builder.mutation({
      query: ({ storeId, promotionId, scheduleId }) => ({
        url: `/admin/catalog/promotions/${promotionId}/schedules/${scheduleId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Promotion"],
    }),

    // Upsell Campaigns
    getUpsellCampaigns: builder.query({
      query: (storeId) => `/admin/catalog/upsell-campaigns?storeId=${storeId}`,
      providesTags: ["UpsellCampaign"],
    }),
    getUpsellCampaign: builder.query({
      query: ({ storeId, campaignId }) =>
        `/admin/catalog/upsell-campaigns/${campaignId}?storeId=${storeId}`,
      providesTags: ["UpsellCampaign"],
    }),
    createUpsellCampaign: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/upsell-campaigns?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),
    updateUpsellCampaign: builder.mutation({
      query: ({ storeId, campaignId, payload }) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),
    deleteUpsellCampaign: builder.mutation({
      query: ({ storeId, campaignId }) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),
    activateUpsellCampaign: builder.mutation({
      query: ({ storeId, campaignId }) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}/activate?storeId=${storeId}`,
        method: "POST",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),
    deactivateUpsellCampaign: builder.mutation({
      query: ({ storeId, campaignId }) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}/deactivate?storeId=${storeId}`,
        method: "POST",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // Tags
    getTags: builder.query({
      query: (storeId) => `/admin/catalog/tags?storeId=${storeId}`,
      providesTags: ["Tag"],
    }),
    createTag: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/tags?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Tag"],
    }),
    updateTag: builder.mutation({
      query: ({ storeId, tagId, payload }) => ({
        url: `/admin/catalog/tags/${tagId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Tag"],
    }),
    deleteTag: builder.mutation({
      query: ({ storeId, tagId }) => ({
        url: `/admin/catalog/tags/${tagId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tag"],
    }),

    // Allergens
    getAllergens: builder.query({
      query: () => "/admin/catalog/allergens",
      providesTags: ["Allergen"],
    }),
    createAllergen: builder.mutation({
      query: (payload) => ({
        url: "/admin/catalog/allergens",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Allergen"],
    }),
    updateAllergen: builder.mutation({
      query: ({ allergenId, payload }) => ({
        url: `/admin/catalog/allergens/${allergenId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Allergen"],
    }),
    deleteAllergen: builder.mutation({
      query: (allergenId) => ({
        url: `/admin/catalog/allergens/${allergenId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Allergen"],
    }),

    // Protein Types
    getProteinTypes: builder.query({
      query: () => "/admin/catalog/protein-types",
      providesTags: ["ProteinType"],
    }),
    createProteinType: builder.mutation({
      query: (payload) => ({
        url: "/admin/catalog/protein-types",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["ProteinType"],
    }),
    updateProteinType: builder.mutation({
      query: ({ proteinTypeId, payload }) => ({
        url: `/admin/catalog/protein-types/${proteinTypeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ProteinType"],
    }),
    deleteProteinType: builder.mutation({
      query: (proteinTypeId) => ({
        url: `/admin/catalog/protein-types/${proteinTypeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProteinType"],
    }),

    // Menu
    getMenu: builder.query({
      query: (storeId) => `/admin/catalog/menu?storeId=${storeId}`,
      providesTags: ["Menu"],
    }),
    getSlots: builder.query({
      query: ({ storeId } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        return `/admin/catalog/slots${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["Slot"],
    }),
    createSlot: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/catalog/slots?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Slot"],
    }),
    updateSlot: builder.mutation({
      query: ({ storeId, slotId, payload }) => ({
        url: `/admin/catalog/slots/${slotId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Slot", "SlotDay", "ItemSlot"],
    }),
    deleteSlot: builder.mutation({
      query: ({ storeId, slotId }) => ({
        url: `/admin/catalog/slots/${slotId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Slot", "SlotDay", "ItemSlot"],
    }),
    getSlotDays: builder.query({
      query: ({ storeId, slotId }) =>
        `/admin/catalog/slots/${slotId}/days?storeId=${storeId}`,
      providesTags: ["SlotDay"],
    }),
    replaceSlotDays: builder.mutation({
      query: ({ storeId, slotId, dayOfWeeks }) => ({
        url: `/admin/catalog/slots/${slotId}/days?storeId=${storeId}`,
        method: "PUT",
        body: { dayOfWeeks: Array.isArray(dayOfWeeks) ? dayOfWeeks : [] },
      }),
      invalidatesTags: ["SlotDay", "Slot"],
    }),
    getItemSlots: builder.query({
      query: ({ storeId, itemId } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (itemId) params.append("itemId", itemId);
        return `/admin/catalog/item-slots${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["ItemSlot"],
    }),
    replaceItemSlots: builder.mutation({
      query: ({ storeId, itemId, slotIds }) => ({
        url: `/admin/catalog/item-slots?storeId=${storeId}&itemId=${itemId}`,
        method: "PUT",
        body: { slotIds: Array.isArray(slotIds) ? slotIds : [] },
      }),
      invalidatesTags: ["ItemSlot"],
    }),
    getItemSlotDays: builder.query({
      query: ({ storeId, itemId, dayOfWeek } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (itemId) params.append("itemId", itemId);
        if (dayOfWeek !== undefined && dayOfWeek !== null && dayOfWeek !== "") {
          params.append("dayOfWeek", dayOfWeek);
        }
        return `/admin/catalog/item-slot-days${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["ItemSlot"],
    }),
    getItemSlotDaysBySlot: builder.query({
      query: ({ storeId, slotId, dayOfWeek } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (slotId) params.append("slotId", slotId);
        if (dayOfWeek !== undefined && dayOfWeek !== null && dayOfWeek !== "") {
          params.append("dayOfWeek", dayOfWeek);
        }
        return `/admin/catalog/item-slot-days/by-slot${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["ItemSlot"],
    }),
    replaceItemSlotDays: builder.mutation({
      query: ({ storeId, itemId, assignments }) => ({
        url: `/admin/catalog/item-slot-days?storeId=${storeId}&itemId=${itemId}`,
        method: "PUT",
        body: {
          assignments: Array.isArray(assignments) ? assignments : [],
        },
      }),
      invalidatesTags: ["ItemSlot"],
    }),
    getAvailabilityWindows: builder.query({
      query: ({ storeId, itemId } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (itemId) params.append("itemId", itemId);
        return `/admin/catalog/availability-windows${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["AvailabilityWindow"],
    }),
    replaceAvailabilityWindows: builder.mutation({
      query: ({ storeId, itemId, payload }) => ({
        url: `/admin/catalog/availability-windows?storeId=${storeId}&itemId=${itemId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["AvailabilityWindow"],
    }),
    deleteAvailabilityWindow: builder.mutation({
      query: ({ storeId, itemId, windowId }) => ({
        url: `/admin/catalog/availability-windows/${windowId}?storeId=${storeId}&itemId=${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AvailabilityWindow"],
    }),

    // Day Overrides
    getDayOverrides: builder.query({
      query: ({ storeId, from, to } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        return `/admin/catalog/day-overrides${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["DayOverride"],
    }),
    upsertDayOverride: builder.mutation({
      query: ({ storeId, targetDate, payload }) => ({
        url: `/admin/catalog/day-overrides/${targetDate}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["DayOverride"],
    }),
    deleteDayOverride: builder.mutation({
      query: ({ storeId, targetDate }) => ({
        url: `/admin/catalog/day-overrides/${targetDate}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DayOverride"],
    }),

    // Reservations
    getReservationPolicy: builder.query({
      query: ({ storeId }) => `/admin/reservations/policy?storeId=${storeId}`,
      providesTags: ["Reservation"],
    }),
    upsertReservationPolicy: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/reservations/policy?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    getReservationSettings: builder.query({
      query: ({ storeId }) => `/admin/reservations/settings?storeId=${storeId}`,
      providesTags: ["Reservation"],
    }),
    upsertReservationSettings: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/reservations/settings?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    getReservationWeeklySlots: builder.query({
      query: ({ storeId, dayOfWeek } = {}) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (dayOfWeek) params.append("dayOfWeek", dayOfWeek);
        return `/admin/reservations/weekly-slots?${params.toString()}`;
      },
      providesTags: ["Reservation"],
    }),
    getReservationExceptions: builder.query({
      query: ({ storeId, from, to }) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (from) params.append("fromDate", from);
        if (to) params.append("toDate", to);
        return `/admin/reservations/exceptions?${params.toString()}`;
      },
      providesTags: ["Reservation"],
    }),
    createReservationException: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/reservations/exceptions?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    updateReservationException: builder.mutation({
      query: ({ storeId, exceptionId, payload }) => ({
        url: `/admin/reservations/exceptions?storeId=${storeId}&exceptionId=${exceptionId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    deleteReservationException: builder.mutation({
      query: ({ storeId, exceptionId }) => ({
        url: `/admin/reservations/exceptions/${exceptionId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reservation"],
    }),
    getReservationSlots: builder.query({
      query: ({ storeId, date, day }) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (date) params.append("date", date);
        if (day !== undefined && day !== null && day !== "") params.append("day", day);
        return `/admin/reservations/slots?${params.toString()}`;
      },
      providesTags: ["Reservation"],
    }),
    generateReservationSlots: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/reservations/slots/generate?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    materializeReservationSlots: builder.mutation({
      query: ({ storeId, from, to }) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        return {
          url: `/admin/reservations/slots/materialize?${params.toString()}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Reservation"],
    }),
    updateReservationSlotCapacity: builder.mutation({
      query: ({ storeId, slotId, payload, date, day }) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (date) params.append("date", date);
        if (day !== undefined && day !== null && day !== "") params.append("day", day);
        return {
          url: `/admin/reservations/slots/${slotId}/capacity?${params.toString()}`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["Reservation"],
    }),
    getReservations: builder.query({
      query: ({ storeId, date, status, search, page = 1, pageSize = 20 } = {}) => {
        const params = new URLSearchParams();
        params.append("storeId", storeId);
        if (date) params.append("date", date);
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        params.append("page", page);
        params.append("pageSize", pageSize);
        return `/admin/reservations?${params.toString()}`;
      },
      providesTags: ["Reservation"],
    }),
    createReservation: builder.mutation({
      query: (payload) => ({
        url: "/admin/reservations",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    updateReservation: builder.mutation({
      query: ({ reservationId, payload }) => ({
        url: `/admin/reservations/${reservationId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    cancelReservation: builder.mutation({
      query: ({ reservationId, reason }) => ({
        url: `/admin/reservations/${reservationId}/cancel${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`,
        method: "POST",
      }),
      invalidatesTags: ["Reservation"],
    }),

    // Order Types
    getOrderTypes: builder.query({
      query: (storeId) => `/admin/stores/${storeId}/order-types`,
      providesTags: ["OrderType"],
    }),
    updateOrderTypes: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/stores/${storeId}/order-types`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["OrderType"],
    }),

    // Public Ordering API
    getStoreBranding: builder.query({
      query: (storeSlug) => `/api/v1/stores/${storeSlug}/ordering/branding`,
      providesTags: ["Branding"],
    }),
    getOrderingCatalog: builder.query({
      query: (storeSlug) => `/api/v1/stores/${storeSlug}/ordering/catalog`,
      providesTags: ["Menu"],
    }),

    // Reporting
    getCompletedOrders: builder.query({
      query: ({
        storeIds = [],
        from,
        to,
        channels = [],
        paymentStatuses = [],
        search,
        page = 1,
        pageSize = 20,
      } = {}) => {
        const params = new URLSearchParams();
        storeIds.forEach((storeId) => params.append("storeIds", storeId));
        channels.forEach((channel) => params.append("channels", channel));
        paymentStatuses.forEach((status) =>
          params.append("paymentStatuses", status),
        );
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (search) params.append("search", search);
        params.append("page", page);
        params.append("pageSize", pageSize);
        return `/admin/reporting/completed-orders?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getSalesSummary: builder.query({
      query: ({ storeIds = [], from, to } = {}) => {
        const params = new URLSearchParams();
        storeIds.forEach((storeId) => params.append("storeIds", storeId));
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        return `/admin/reporting/sales-summary?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getPaymentSummary: builder.query({
      query: ({ storeIds = [], from, to } = {}) => {
        const params = new URLSearchParams();
        storeIds.forEach((storeId) => params.append("storeIds", storeId));
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        return `/admin/reporting/payment-summary?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getHourlyDemand: builder.query({
      query: ({ storeIds = [], from, to } = {}) => {
        const params = new URLSearchParams();
        storeIds.forEach((storeId) => params.append("storeIds", storeId));
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        return `/admin/reporting/hourly-demand?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getTopItemsCategoryMix: builder.query({
      query: ({ storeIds = [], from, to, limit = 10 } = {}) => {
        const params = new URLSearchParams();
        storeIds.forEach((storeId) => params.append("storeIds", storeId));
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (limit) params.append("limit", limit);
        return `/admin/reporting/top-items-category-mix?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getCashingUpSessionsReport: builder.query({
      query: ({
        storeId,
        businessDate,
        from,
        to,
        tillId,
        statuses = [],
        limit = 20,
      } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (!from && !to && businessDate) {
          params.append("businessDate", businessDate);
        }
        if (tillId) params.append("tillId", tillId);
        statuses.forEach((status) => params.append("statuses", status));
        if (limit) params.append("limit", limit);
        return `/api/reports/cashing-up/sessions?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getCashingUpSession: builder.query({
      query: ({ cashSessionId, storeId } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        return `/api/reports/cashing-up/sessions/${cashSessionId}${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["Reporting"],
    }),
    getCashingUpVariancesReport: builder.query({
      query: ({ storeId, businessDate, from, to, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (!from && !to && businessDate) params.append("businessDate", businessDate);
        if (limit) params.append("limit", limit);
        return `/api/reports/cashing-up/variances?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getCashingUpPaidOutsReport: builder.query({
      query: ({ storeId, from, to, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (limit) params.append("limit", limit);
        return `/api/reports/cashing-up/paid-outs?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),
    getCashingUpDayClosesReport: builder.query({
      query: ({ storeId, businessDate, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        if (businessDate) params.append("businessDate", businessDate);
        if (limit) params.append("limit", limit);
        return `/api/reports/cashing-up/day-closes?${params.toString()}`;
      },
      providesTags: ["Reporting"],
    }),

    // Users
    getUsers: builder.query({
      query: ({ businessId, status, q } = {}) => {
        const params = new URLSearchParams();
        if (businessId) params.append("businessId", businessId);
        if (status) params.append("status", status);
        if (q) params.append("q", q);
        return `/admin/users${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["User"],
    }),
    getUser: builder.query({
      query: (userId) => `/admin/users/${userId}`,
      providesTags: ["User"],
    }),
    createUser: builder.mutation({
      query: (payload) => ({
        url: "/admin/users",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["User"],
    }),
    updateUser: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/admin/users/${userId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["User"],
    }),
    resetUserPassword: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/admin/users/${userId}/reset-password`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    // Roles
    getRoles: builder.query({
      query: ({ businessId } = {}) => {
        const params = new URLSearchParams();
        if (businessId) params.append("businessId", businessId);
        return `/admin/roles${params.toString() ? `?${params.toString()}` : ""}`;
      },
      providesTags: ["Role"],
    }),
    getRole: builder.query({
      query: (roleId) => `/admin/roles/${roleId}`,
      providesTags: ["Role"],
    }),
    createRole: builder.mutation({
      query: (payload) => ({
        url: "/admin/roles",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Role"],
    }),
    updateRole: builder.mutation({
      query: ({ roleId, payload }) => ({
        url: `/admin/roles/${roleId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Role"],
    }),
    deleteRole: builder.mutation({
      query: (roleId) => ({
        url: `/admin/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Role"],
    }),

    // Permissions
    getPermissions: builder.query({
      query: () => "/admin/permissions",
      providesTags: ["Permission"],
    }),
    getPermission: builder.query({
      query: (permissionId) => `/admin/permissions/${permissionId}`,
      providesTags: ["Permission"],
    }),
    createPermission: builder.mutation({
      query: (payload) => ({
        url: "/admin/permissions",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Permission"],
    }),
    updatePermission: builder.mutation({
      query: ({ permissionId, payload }) => ({
        url: `/admin/permissions/${permissionId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Permission"],
    }),
    deletePermission: builder.mutation({
      query: (permissionId) => ({
        url: `/admin/permissions/${permissionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Permission"],
    }),

    // Role Permissions
    getRolePermissions: builder.query({
      query: (roleId) => `/admin/roles/${roleId}/permissions`,
      providesTags: ["RolePermission"],
    }),
    updateRolePermissions: builder.mutation({
      query: ({ roleId, payload }) => ({
        url: `/admin/roles/${roleId}/permissions`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["RolePermission", "Role"],
    }),

    // User Roles
    getUserRoles: builder.query({
      query: (userId) => `/admin/users/${userId}/roles`,
      providesTags: ["UserRole"],
    }),
    createUserRole: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/admin/users/${userId}/roles`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["UserRole", "User"],
    }),
    deleteUserRole: builder.mutation({
      query: ({ userId, userRoleId }) => ({
        url: `/admin/users/${userId}/roles/${userRoleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserRole", "User"],
    }),

    // Store QR Configuration
    getQRConfig: builder.query({
      query: (storeId) => `/admin/stores/${storeId}/qr-config`,
      providesTags: ["QRConfig"],
    }),
    updateQRConfig: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/stores/${storeId}/qr-config`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["QRConfig"],
    }),

    // Floorplan - Floors
    getFloors: builder.query({
      query: (storeId) => `/admin/floorplan/floors?storeId=${storeId}`,
      providesTags: ["Floor"],
    }),
    createFloor: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/floorplan/floors?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Floor"],
    }),
    updateFloor: builder.mutation({
      query: ({ storeId, floorId, payload }) => ({
        url: `/admin/floorplan/floors/${floorId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Floor"],
    }),
    deleteFloor: builder.mutation({
      query: ({ storeId, floorId }) => ({
        url: `/admin/floorplan/floors/${floorId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Floor"],
    }),

    // Floorplan - Sections
    getSections: builder.query({
      query: (storeId) => `/admin/floorplan/sections?storeId=${storeId}`,
      providesTags: ["Section"],
    }),
    createSection: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/floorplan/sections?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Section"],
    }),
    updateSection: builder.mutation({
      query: ({ storeId, sectionId, payload }) => ({
        url: `/admin/floorplan/sections/${sectionId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Section"],
    }),
    deleteSection: builder.mutation({
      query: ({ storeId, sectionId }) => ({
        url: `/admin/floorplan/sections/${sectionId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Section"],
    }),

    // Floorplan - Tables
    getTables: builder.query({
      query: (storeId) => `/admin/floorplan/tables?storeId=${storeId}`,
      providesTags: ["Table"],
    }),
    createTable: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/admin/floorplan/tables?storeId=${storeId}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Table"],
    }),
    updateTable: builder.mutation({
      query: ({ storeId, tableId, payload }) => ({
        url: `/admin/floorplan/tables/${tableId}?storeId=${storeId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Table"],
    }),
    deleteTable: builder.mutation({
      query: ({ storeId, tableId }) => ({
        url: `/admin/floorplan/tables/${tableId}?storeId=${storeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Table"],
    }),
    downloadTableQRCodes: builder.query({
      query: (storeId) => `/admin/floorplan/tables/qr-codes?storeId=${storeId}`,
      providesTags: ["Table"],
    }),
  }),
});

export const {
  useLoginSuperAdminMutation,
  useLoginBusinessAdminMutation,
  useGetBusinessesQuery,
  useGetBusinessQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useGetBusinessStoresQuery,
  useGetBusinessStoreQuery,
  useCreateBusinessStoreMutation,
  useUpdateBusinessStoreMutation,
  useUpdateBusinessStoreStatusMutation,
  useGetStoreFeaturesQuery,
  useUpdateStoreFeaturesMutation,
  useUploadBusinessLogoMutation,
  useDeleteBusinessLogoMutation,
  useGetBusinessFeaturesQuery,
  useUpdateBusinessFeaturesMutation,
  useGetBusinessGeneralSettingsQuery,
  useUpdateBusinessGeneralSettingsMutation,
  useGetBusinessTipPresetsQuery,
  useUpdateBusinessTipPresetsMutation,
  useGetPaymentGatewaysQuery,
  useUpdateStripeGatewayMutation,
  useGetLicensesQuery,
  useAllocateLicenseMutation,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useGetModifierGroupsQuery,
  useCreateModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useDeleteModifierGroupMutation,
  useGetModifierOptionsQuery,
  useCreateModifierOptionMutation,
  useUpdateModifierOptionMutation,
  useDeleteModifierOptionMutation,
  useUpdateItemModifierGroupsMutation,
  useGetBundlesQuery,
  useGetBundleQuery,
  useGetBundleTypesQuery,
  useCreateBundleMutation,
  useUpdateBundleMutation,
  useDeleteBundleMutation,
  useCreateBundleVariantMutation,
  useUpdateBundleVariantMutation,
  useDeleteBundleVariantMutation,
  useCreateBundleComponentMutation,
  useUpdateBundleComponentMutation,
  useDeleteBundleComponentMutation,
  useCreateBundleComponentItemMutation,
  useUpdateBundleComponentItemMutation,
  useDeleteBundleComponentItemMutation,
  useCreateCategoryBundleMutation,
  useUpdateCategoryBundleMutation,
  useDeleteCategoryBundleMutation,
  useGetPromotionsQuery,
  useGetPromotionQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useDeletePromotionMutation,
  useCreatePromotionConditionMutation,
  useUpdatePromotionConditionMutation,
  useDeletePromotionConditionMutation,
  useCreatePromotionEffectMutation,
  useUpdatePromotionEffectMutation,
  useDeletePromotionEffectMutation,
  useCreatePromotionScheduleMutation,
  useUpdatePromotionScheduleMutation,
  useDeletePromotionScheduleMutation,
  useGetUpsellCampaignsQuery,
  useGetUpsellCampaignQuery,
  useCreateUpsellCampaignMutation,
  useUpdateUpsellCampaignMutation,
  useDeleteUpsellCampaignMutation,
  useActivateUpsellCampaignMutation,
  useDeactivateUpsellCampaignMutation,
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetAllergensQuery,
  useCreateAllergenMutation,
  useUpdateAllergenMutation,
  useDeleteAllergenMutation,
  useGetProteinTypesQuery,
  useCreateProteinTypeMutation,
  useUpdateProteinTypeMutation,
  useDeleteProteinTypeMutation,
  useGetMenuQuery,
  useGetSlotsQuery,
  useCreateSlotMutation,
  useUpdateSlotMutation,
  useDeleteSlotMutation,
  useLazyGetSlotDaysQuery,
  useReplaceSlotDaysMutation,
  useGetItemSlotsQuery,
  useLazyGetItemSlotsQuery,
  useReplaceItemSlotsMutation,
  useGetItemSlotDaysQuery,
  useLazyGetItemSlotDaysQuery,
  useGetItemSlotDaysBySlotQuery,
  useLazyGetItemSlotDaysBySlotQuery,
  useReplaceItemSlotDaysMutation,
  useGetAvailabilityWindowsQuery,
  useReplaceAvailabilityWindowsMutation,
  useDeleteAvailabilityWindowMutation,
  useGetDayOverridesQuery,
  useUpsertDayOverrideMutation,
  useDeleteDayOverrideMutation,
  useGetReservationPolicyQuery,
  useUpsertReservationPolicyMutation,
  useGetReservationSettingsQuery,
  useUpsertReservationSettingsMutation,
  useGetReservationWeeklySlotsQuery,
  useGetReservationExceptionsQuery,
  useCreateReservationExceptionMutation,
  useUpdateReservationExceptionMutation,
  useDeleteReservationExceptionMutation,
  useGetReservationSlotsQuery,
  useLazyGetReservationSlotsQuery,
  useGenerateReservationSlotsMutation,
  useMaterializeReservationSlotsMutation,
  useUpdateReservationSlotCapacityMutation,
  useGetReservationsQuery,
  useCreateReservationMutation,
  useUpdateReservationMutation,
  useCancelReservationMutation,
  useGetOrderTypesQuery,
  useUpdateOrderTypesMutation,
  useGetStoreBrandingQuery,
  useGetOrderingCatalogQuery,
  useGetCompletedOrdersQuery,
  useGetSalesSummaryQuery,
  useGetPaymentSummaryQuery,
  useGetHourlyDemandQuery,
  useGetTopItemsCategoryMixQuery,
  useGetCashingUpSessionsReportQuery,
  useGetCashingUpSessionQuery,
  useGetCashingUpVariancesReportQuery,
  useGetCashingUpPaidOutsReportQuery,
  useGetCashingUpDayClosesReportQuery,
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useResetUserPasswordMutation,
  useDeleteUserMutation,
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetPermissionQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useDeletePermissionMutation,
  useGetRolePermissionsQuery,
  useUpdateRolePermissionsMutation,
  useGetUserRolesQuery,
  useCreateUserRoleMutation,
  useDeleteUserRoleMutation,
  useGetQRConfigQuery,
  useUpdateQRConfigMutation,
  useGetFloorsQuery,
  useCreateFloorMutation,
  useUpdateFloorMutation,
  useDeleteFloorMutation,
  useGetSectionsQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useGetTablesQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useDeleteTableMutation,
  useDownloadTableQRCodesQuery,
} = api;


